import { Button, Checkbox, Heading, Icon, IconButton, Select, Spinner, Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useToast } from "@chakra-ui/react";
import { useEffect, useState } from "react";

import { FaArrowLeft, FaArrowRight, FaDatabase } from "react-icons/fa";
import { RiFilterOffFill, RiUserSearchFill } from "react-icons/ri";
import { GET } from "../../../utils/ApiRequestProvider";
import { formatElapsed, formatDurationMs } from "../../../utils/processingNotes";

interface PaginationType {
    currentPage: number;
    totalPages: number;
    totalRecords: number;
}

export default function UserActivity() {
    const toast = useToast();
    const [invs, setInvs] = useState<Array<any>>([]);
    const [loading, setLoading] = useState(false);
    const [paginationQuery, setPaginationQuery] = useState({ page: 1, pageSize: 10 });
    const [pagination, setPagination] = useState<PaginationType>({ currentPage: 0, totalPages: 0, totalRecords: 0 });
    const [includeTest, setIncludeTest] = useState(false);
    const [includeAdmin, setIncludeAdmin] = useState(false);
    const [includeDataEntry, setIncludeDataEntry] = useState(false);
    const [, setTick] = useState(0);

    useEffect(() => {
        const id = setInterval(() => setTick((n) => n + 1), 60000);
        return () => clearInterval(id);
    }, []);

    const clientDuration = (login?: string | null, logout?: string | null): string => {
        if (!login || !logout) return 'Active';
        const start = new Date(login).getTime();
        const end = new Date(logout).getTime();
        if (Number.isNaN(start) || Number.isNaN(end)) return '';
        return formatDurationMs(end - start);
    };

    async function FetchList() {
        setLoading(true);
        try {
            const query = new URLSearchParams({
                page: String(paginationQuery.page),
                pageSize: String(paginationQuery.pageSize),
                includeTest: String(includeTest),
                includeAdmin: String(includeAdmin),
                includeDataEntry: String(includeDataEntry),
            });
            const res = await GET(`/api/internal/user-activity?${query.toString()}`);
            setInvs(res.data.documents ?? []);
            setPagination(res.data.pagination);
        } catch (err) {
            toast({
                title: 'Something Went Wrong!',
                description: 'An unexpected error occured while proceeding your request!',
                status: 'error',
                isClosable: false,
                duration: 1000,
                position: 'top-right'
            });
        }
        setLoading(false);
    }

    useEffect(() => {
        FetchList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationQuery, includeTest, includeAdmin, includeDataEntry]);

    const convertDate = (date: string | null) => (date ? new Date(date).toLocaleString() : '');

    return (
        <Stack w={'full'} justify={"center"} align={"center"} spacing={6}>
            <Stack
                direction={{ base: 'column', md: 'column', lg: 'column', xl: 'row' }}
                justify={'space-between'}
                boxShadow={'base'}
                rounded={'xl'}
                w={'98%'}
                p={4}
                spacing={{ base: 6, md: 6, lg: 6, xl: 0 }}
            >
                <Stack
                    align={'flex-start'}
                    justify={'start'}
                    spacing={4}
                >
                    <Stack direction={'row'}>
                        <Icon
                            as={RiUserSearchFill}
                            fontSize={'5xl'}
                            color={'primary.400'}
                        />
                        <Stack spacing={1}>
                            <Heading fontSize={'xl'}>User Activity</Heading>
                            <Text fontSize={'sm'}>Here you can see all your users log.</Text>
                        </Stack>
                    </Stack>
                </Stack>
                <Stack align={{ lg: 'flex-start', xl: 'flex-end' }} justify={'end'} spacing={4}>
                    <Stack direction={'row'}>
                        <Stack spacing={4} direction='row'>
                            <Checkbox size={'sm'} colorScheme='primary.500' isChecked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)}>
                                Include Test
                            </Checkbox>
                            <Checkbox size={'sm'} colorScheme='primary.500' isChecked={includeAdmin} onChange={(e) => setIncludeAdmin(e.target.checked)}>
                                Include Admin
                            </Checkbox>
                            <Checkbox size={'sm'} colorScheme='primary.500' isChecked={includeDataEntry} onChange={(e) => setIncludeDataEntry(e.target.checked)}>
                                Include Data Entry
                            </Checkbox>
                        </Stack>
                        <Button
                            size={'sm'}
                            leftIcon={<FaDatabase />}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                            onClick={() => setPaginationQuery({ page: 1, pageSize: pagination.totalRecords || 1000 })}
                        >
                            Get All Data
                        </Button>
                        <Button
                            size={'sm'}
                            leftIcon={<RiFilterOffFill />}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                            onClick={() => { setIncludeTest(false); setIncludeAdmin(false); setIncludeDataEntry(false); setPaginationQuery({ page: 1, pageSize: 10 }); }}
                        >
                            Clear Filter
                        </Button>
                    </Stack>
                </Stack>
            </Stack>

            <Stack boxShadow={'base'} rounded={'xl'} w={'98%'} p={4} spacing={4}>
                <TableContainer h={'sm'} overflowY={'scroll'}>
                    <Table size='sm'>
                        <Thead position={'sticky'} top={'0%'} h={'50px'} bg={'primary.400'} alignItems={'flex-start'}>
                            <Tr>
                                <Th
                                    isNumeric
                                    color={"white"}
                                >
                                    Sr.
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Country
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Org Name
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Org Id
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Entity Name
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Entity Id
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    User Name
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    User Id
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Login IP
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Login Time
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Login Time Elapsed
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Logout Time
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Logout Time Elapsed
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Duration
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Device
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Browser
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Version
                                </Th>
                                <Th
                                    color={'white'}
                                    colSpan={2}
                                >
                                    Actions
                                </Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {
                                invs.length > 0 ? (
                                    invs.map((inv, i) => (
                                        <Tr key={i}>
                                            <Td>{i + 1}</Td>
                                            <Td>{inv.country}</Td>
                                            <Td>{inv.orgName}</Td>
                                            <Td>{inv.orgId}</Td>
                                            <Td>{inv.entityName}</Td>
                                            <Td>{inv.entityId}</Td>
                                            <Td>{inv.userName}</Td>
                                            <Td>{inv.userid}</Td>
                                            <Td>{inv.loginIP}</Td>
                                            <Td>{convertDate(inv.logInTime)}</Td>
                                            <Td>{formatElapsed(inv.logInTime)}</Td>
                                            <Td>{convertDate(inv.logOutTime)}</Td>
                                            <Td>{inv.logOutTime ? formatElapsed(inv.logOutTime) : 'Active'}</Td>
                                            <Td>{clientDuration(inv.logInTime, inv.logOutTime)}</Td>
                                            <Td>{inv.device}</Td>
                                            <Td>{inv.browser}</Td>
                                            <Td>{inv.version}</Td>
                                            <Td colSpan={2}>
                                                <Text>{inv.action}</Text>
                                                <Text fontSize={'xs'} color={'gray.500'}>{inv.description}</Text>
                                            </Td>
                                        </Tr>
                                    ))
                                ) : (
                                    <Tr>
                                        <Td colSpan={19} textAlign={'center'}>
                                            {loading ? <Spinner size={'sm'} color={'primary.400'} /> : 'No Data Available'}
                                        </Td>
                                    </Tr>
                                )
                            }
                        </Tbody>
                    </Table>
                </TableContainer>
                <Stack
                    direction={'row'}
                    justify={"space-between"}
                    align={'center'}
                >
                    <Text>Showing {invs.length} of {pagination.totalRecords} results</Text>
                    <Stack direction={'row'} spacing={4}>
                        <Select defaultValue={10} onChange={(e) => setPaginationQuery({ page: 1, pageSize: parseInt(e.target.value) })}>
                            <option value={5}>5 Per Page</option>
                            <option value={10}>10 Per Page</option>
                            <option value={25}>25 Per Page</option>
                            <option value={50}>50 Per Page</option>
                        </Select>
                        <Stack direction={'row'}>
                            <IconButton
                                aria-label="Previous"
                                icon={<FaArrowLeft />}
                                rounded={'full'}
                                disabled={pagination.currentPage <= 1}
                                onClick={() => setPaginationQuery({ ...paginationQuery, page: pagination.currentPage - 1 })}
                            />
                            <IconButton
                                aria-label="Next"
                                icon={<FaArrowRight />}
                                rounded={'full'}
                                disabled={pagination.currentPage >= pagination.totalPages}
                                onClick={() => setPaginationQuery({ ...paginationQuery, page: pagination.currentPage + 1 })}
                            />
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
        </Stack >
    )
}